/**
 * RowPresenceIndicator - Shows who's editing a specific row
 * 
 * Features:
 * - Small avatar with colored border indicating who's editing
 * - Pulsing animation to draw attention
 * - Tooltip showing user name
 * - Auto-hides after editing timeout
 */

import React, { useState } from "react";
import { usePresence, getInitials } from "../../context/PresenceContext";

// ============================================================
// MAIN COMPONENT - Row-level indicator
// ============================================================
export default function RowPresenceIndicator({ rowId }) {
  const { isRowBeingEdited, isEnabled } = usePresence();
  const [imageError, setImageError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (!isEnabled) return null;
  
  const editingUser = isRowBeingEdited(rowId);
  if (!editingUser) return null;
  
  const showImage = editingUser.avatar && !imageError;
  
  return (
    <div 
      style={styles.container}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div 
        style={{
          ...styles.indicator,
          borderColor: editingUser.color,
        }}
      >
        {showImage ? (
          <img 
            src={editingUser.avatar} 
            alt={editingUser.name}
            style={styles.avatar}
            onError={() => setImageError(true)}
          />
        ) : (
          <div 
            style={{
              ...styles.initials,
              background: editingUser.color,
            }}
          >
            {getInitials(editingUser.name)}
          </div>
        )}
        <div style={styles.pulse} />
      </div>
      
      {/* Tooltip */}
      {showTooltip && (
        <div style={styles.tooltip}>
          <span style={styles.tooltipName}>{editingUser.name}</span>
          <span style={styles.tooltipAction}>is editing...</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPACT VERSION - For tight spaces (e.g., in cells)
// ============================================================
export function RowPresenceIndicatorCompact({ rowId }) {
  const { isRowBeingEdited, isEnabled } = usePresence();
  
  if (!isEnabled) return null;
  
  const editingUser = isRowBeingEdited(rowId);
  if (!editingUser) return null;
  
  return (
    <div 
      style={styles.compactContainer}
      title={`${editingUser.name} is editing...`}
    >
      <div 
        style={{
          ...styles.compactDot,
          background: editingUser.color,
        }}
      />
    </div>
  );
}

// ============================================================
// ROW WRAPPER - Adds border glow to entire row when being edited
// ============================================================
export function RowPresenceWrapper({ rowId, children, style = {} }) {
  const { isRowBeingEdited, isEnabled } = usePresence();
  
  const editingUser = isEnabled ? isRowBeingEdited(rowId) : null;
  
  if (!editingUser) {
    return <div style={style}>{children}</div>;
  }
  
  return (
    <div 
      style={{
        ...style,
        position: 'relative',
        boxShadow: `0 0 0 2px ${editingUser.color}40, 0 0 8px ${editingUser.color}30`,
        borderRadius: style.borderRadius || 8,
        transition: 'box-shadow 0.3s ease',
      }}
    >
      {/* Editing badge */}
      <div 
        style={{
          position: 'absolute',
          top: -8,
          right: 12,
          background: editingUser.color,
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 10,
        }}
      >
        <span>{editingUser.name.split(' ')[0]}</span>
        <span style={{ opacity: 0.8 }}>editing</span>
      </div>
      {children}
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  indicator: {
    position: 'relative',
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '2px solid',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  },
  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  initials: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    fontWeight: 600,
    color: '#fff',
  },
  pulse: {
    position: 'absolute',
    inset: -4,
    borderRadius: '50%',
    border: '2px solid currentColor',
    opacity: 0,
    animation: 'presencePulse 2s ease-out infinite',
  },
  tooltip: {
    position: 'absolute',
    bottom: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#1f2937',
    borderRadius: 6,
    padding: '4px 8px',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },
  tooltipName: {
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
  },
  tooltipAction: {
    fontSize: 10,
    color: '#9CA3AF',
  },
  compactContainer: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  compactDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    animation: 'presencePulse 2s ease-out infinite',
  },
};

// Add keyframes for pulse animation
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('presence-animations');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'presence-animations';
    styleSheet.textContent = `
      @keyframes presencePulse {
        0% { transform: scale(1); opacity: 0.8; }
        50% { transform: scale(1.3); opacity: 0; }
        100% { transform: scale(1); opacity: 0; }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}
